import { Component, OnInit, ViewChild } from '@angular/core';
import { ReporteLogoService } from '../../../services/database/reporteLogo.service';
import { AccessRole, ButtonColumnType, CustomTableComponent, CustomTitle, DataColumnType, FormGroupErrorMessageComponent, ICustomTable, IHeader, LoadingPanelService, LocalService, noWhitespaceValidator, Utils, VisorComponent } from 'lib-base';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin, fromEventPattern } from 'rxjs';
import { IGetReporteLogos, ReporteLogoDto } from '../../../shared/interfaces/database/configuracion';
import { ConfirmPopupModule } from "primeng/confirmpopup";
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
  selector: 'app-reporte-logos',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, CustomTableComponent, ConfirmPopupModule, DialogModule, CustomTitle, ButtonModule,
    TranslateModule, FormGroupErrorMessageComponent, InputTextModule, FileUploadModule, VisorComponent
  ],
  providers: [ConfirmationService],
  template: `
    <p-confirmPopup></p-confirmPopup>
    <app-visor/>
    <custom-table [config]="customTable">
    </custom-table>

    <section>
      <form [formGroup]="form"(ngSubmit)="onSubmit()" >
        <p-dialog [(visible)]="displayModal" [modal]="true" 
          [autoZIndex]="true" [draggable]="false" [resizable]="true" [maximizable]="true" 
          [breakpoints]="{'640px': '100vw'}" [style]="{width: '80vw'}">

          <ng-template pTemplate="header">
            <CustomTitle [title]="formTitle" />
          </ng-template>

          <ng-template pTemplate="content">
            <main class="grid formgrid p-fluid">
                <div class="field col-12 md:col-6">
                  <label class="ml-3" for="nombre">{{trans.nombre}}<span style="color: red">*</span></label>
                  <input pInputText formControlName="nombre" id="nombre" type="text" />
                  <ng-container *ngIf="form.get('nombre')?.invalid && form.get('nombre')?.touched">
                    <app-form-group-error-message [control]="form.get('nombre')"></app-form-group-error-message>
                  </ng-container>
                </div>
                <div class="field col-12 md:col-6">
                  <label class="ml-3" for="descripcion">{{trans.descripcion}}<span style="color: red">*</span></label>
                  <input pInputText formControlName="descripcion" id="descripcion" type="text"/>
                  <ng-container *ngIf="form.get('descripcion')?.invalid && form.get('descripcion')?.touched">
                    <app-form-group-error-message [control]="form.get('descripcion')"></app-form-group-error-message>
                  </ng-container>
                </div>
                <div class="col-12">
                <div class="flex">
                  <p-fileUpload #fileUpload mode="basic"
                    accept="image/*" [maxFileSize]="uploadFileSize"
                    [chooseLabel]="'msg.agregarDocumento' | translate"
                    [customUpload]="true" [auto]="true" chooseIcon="pi pi-upload"
                    (uploadHandler)="uploadHandler($event, fileUpload)"></p-fileUpload>
                  <button pButton icon="pi pi-trash" class="p-button-cancel-fileupload cancel border-noround-left	" (click)="removeFile()"
                          [disabled]="!archivo"></button>
                </div>

                @if (invalidSize) {
                  <small class="p-error block">
                    <strong>{{ archivo?.name }}</strong>: {{ "form.msg.invalid-size" | translate }} </small>
                } @else if (archivo?.size && !invalidSize) {
                  <small class="text-gray-700 block"> {{ archivo?.name }} </small>
                } @else if (requieredFile) {
                  <small class="p-error block">{{"msg.campo-requerido" | translate}}</small>
                }
              </div>                   
            </main>
          </ng-template>
              
          <ng-template pTemplate="footer">
            <div class="field col-12">
              <div class="flex justify-content-between">
                <button pButton type="button" [label]="'form.button.limpiar' | translate" iconPos="right" icon="pi pi-eraser" class="tax-app-button"
                style="width: 16rem;" (click)="resetForm()"></button>
                <button pButton type="submit" [label]="'form.button.guardar' | translate" icon="pi pi-save" iconPos="right" class="tax-app-button"
                style="width: 16rem;" [disabled]="!ls.hasRole(role.ADMIN_TRAM)"></button>
              </div>
            </div>
          </ng-template>
        </p-dialog>
    </form>
    </section>
  `
})
export class ReporteLogosComponent implements OnInit {
  trans: any = {};
  role = AccessRole;
  customTable !: ICustomTable;
  @ViewChild(CustomTableComponent) customTableComponent !: CustomTableComponent;
  displayModal = false; 
  @ViewChild(VisorComponent) visorDialog !: VisorComponent;

  //Formulario
  form !: FormGroup;
  formTitle = '';
  _editReporteLogo !: IGetReporteLogos | undefined;
  archivo?: File = undefined;
  readonly uploadFileSize: number = 1024 * 1024
  invalidSize = false;
  requieredFile = false;

  get isNewForm(): boolean {
    return this._editReporteLogo === undefined;
  }

  constructor(private readonly _rls: ReporteLogoService, private readonly _t: TranslateService, public ls: LocalService, private readonly _fb: FormBuilder,
    private readonly _ms: MessageService, private readonly _cs: ConfirmationService, private readonly _lps: LoadingPanelService
  ) {
    this.configureForm();
  }

  ngOnInit(): void {
    this.translate().then(() => {
      this.configureTable();
    });
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      nombre: this._t.get('common.nombre'),
      descripcion: this._t.get('common.descripcion'),
      nuevoReporteLogo: this._t.get('msg.nuevoReporteLogo'),
      editarReporteLogo: this._t.get('msg.editarReporteLogo'),
      reporteLogoGuardadoExitosamente: this._t.get('msg.reporteLogoGuardadoExitosamente'),
      noCambiosFormulario: this._t.get('msg.noCambiosFormulario'),
      completeCamposRequeridos: this._t.get('msg.completeCamposRequeridos'),
      confirmarEliminarFila: this._t.get('msg.confirmarEliminarFila'),
      registroEliminado: this._t.get('msg.registroEliminado'),
      exito: this._t.get('common.exito'),
      advertencia: this._t.get('common.advertencia'),
      logo: this._t.get('common.logo'),
    }));
  }

  private configureTable() {
    this.customTable = {
      header: {
      } as IHeader,
      columns: [
        {
          field: 'nombre',
          header: this.trans.nombre,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          dataType: DataColumnType.TEXT
        },
        {
          field: 'descripcion',
          header: this.trans.descripcion,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          dataType: DataColumnType.TEXT
        },
        {
          field: 'logo',
          header: this.trans.logo,
          icon: 'pi pi-search',
          class: 'button-edit p-button-outlined pt-2 pb-2 pr-0',
          width: '0rem',
          severity: 'info',
          buttonType: ButtonColumnType.CUSTOM,
          onClick: (event: Event, data: IGetReporteLogos) => this.displayLogo(data),
        },
        {
          field: 'edit',
          header: '',
          width: '0rem',
          class: 'pt-2 pb-2 pl-1 pr-1',
          buttonType: ButtonColumnType.EDIT,
          disabled: !this.ls.hasRole(this.role.ADMIN_TRAM),
          tooltip: {
            message: this.trans.requiereRolSeguridad
          },
          onClick: (event: Event, data: IGetReporteLogos) => this.openEditModal(data)
        },
        {
          field: 'delete',
          header: '',
          width: '0rem',
          class: 'pt-2 pb-2 pl-0',
          buttonType: ButtonColumnType.DELETE,
          disabled: !this.ls.hasRole(this.role.ADMIN_TRAM),
          tooltip: {
            message: this.trans.requiereRolSeguridad
          },
          onClick: (event: Event, data: IGetReporteLogos) => this.deleteConfirmation(event, data),
          headerBtn: {
            icon: 'pi pi-plus',
            class: 'pt-2 pb-2 pl-0 pr-0',
            onClick: (event: Event) => this.openNewModal(),
            disabled: !this.ls.hasRole(this.role.ADMIN_TRAM),
          },
          headerTooltip: {
            message: this.trans.requiereRolSeguridad,
            disabled: this.ls.hasRole(this.role.ADMIN_TRAM)
          }
        }
      ]
    } as ICustomTable;
  }

  //#region Tabla
  getReporteLogos() {
    this._rls.getReporteLogos().subscribe({
      next: (data: IGetReporteLogos[]) => {
        this.customTableComponent.data = data;
      },
    });
  }

  displayLogo(data: IGetReporteLogos): void {
    this._lps.show(true);
    this._rls.descargarReportePrevisualizacion(data.id).subscribe({
      next: (response: Blob) => {
        this._lps.hide();
        this.visorDialog.showDialog(response);
      },
    });
  }
  //#endregion Tabla

  //#region Delete
  deleteConfirmation(event: Event, data: IGetReporteLogos): void {
    this._cs.confirm({
      target: event.target as EventTarget,
      message: this.trans.confirmarEliminarFila,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.eliminarReporteLogo(data.id);
      },
      reject: () => {

      }
    });
  }

  eliminarReporteLogo(id: number) {
    this._lps.show(true);
    this._rls.eliminarReporteLogo(id).subscribe({
      next: () => {
        this._ms.add({ severity: 'success', summary: this.trans.exito, detail: this.trans.registroEliminado });
        this.getReporteLogos();
        this._lps.hide();
      },
    });
  }
  //#endregion

  //#region Formulario
  configureForm() {
    this.form = this._fb.group({
      nombre: ['', [Validators.required, Validators.maxLength(100), noWhitespaceValidator]],
      descripcion: ['', [Validators.required, Validators.maxLength(255), noWhitespaceValidator]],
    });
  }

  openNewModal(): void {
    this.formTitle = this.trans.nuevoReporteLogo;
    this._editReporteLogo = undefined;
    this.resetForm();
    this.displayModal = true;
  }

  openEditModal(data: IGetReporteLogos): void {
    this.resetForm();
    this.formTitle = this.trans.editarReporteLogo;
    this._editReporteLogo = data;
    this.form.get('nombre')?.setValue(data.nombre);
    this.form.get('descripcion')?.setValue(data.descripcion);
    this.displayModal = true;
  }

  resetForm() {
    this.form.reset();
    this.removeFile();
  }

  removeFile() {
    this.archivo = undefined;
    this.invalidSize = false;
  }

  uploadHandler(event: FileUploadHandlerEvent, fileUpload: FileUpload) {
    const file = event.files[0];
    this.invalidSize = false;
    //validate file size
    if (file.size > this.uploadFileSize) {
      this.invalidSize = true;
      fileUpload.clear();
      return;
    }
    this.archivo = file;
    fileUpload.clear();
  }

  private crearReporteLogo(request: ReporteLogoDto, archivo: File) {
    this._lps.show(true);
    this._rls.crearReporteLogo(request, archivo).subscribe({
      next: () => {
        this._ms.add({ severity: 'success', summary: this.trans.exito, detail: this.trans.reporteLogoGuardadoExitosamente });
        this.displayModal = false;
        this.getReporteLogos();
        this._lps.hide();
      }
    });
  }

  actualizarReporteLogo(request: ReporteLogoDto, archivo ?: File) {
    if(!this._editReporteLogo) return;
    this._lps.show(true);
    this._rls.actualizarReporteLogo(this._editReporteLogo.id, request, archivo).subscribe({
      next: () => {
        this._ms.add({ severity: 'success', summary: this.trans.exito, detail: this.trans.reporteLogoGuardadoExitosamente });
        this.displayModal = false;
        this.getReporteLogos();
        this._lps.hide();
      },
    });
  }

  onSubmit() {
    if (this.form.invalid) {
      Utils.markAllFormControlsAsTouched(this.form);
      this._ms.add({ severity: 'warn', summary: this.trans.advertencia, detail: this.trans.noCambiosFormulario });
      return;
    }

    const missingFile = this.archivo === undefined;
    if (missingFile && this.isNewForm) {
      Utils.markAllFormControlsAsTouched(this.form);
      this.requieredFile = true;
      this._ms.add({ severity: 'warn', summary: this.trans.advertencia, detail: this.trans.completeCamposRequeridos });
      return;
    }

    const request: ReporteLogoDto = {
      nombre: this.form.get('nombre')?.value,
      descripcion: this.form.get('descripcion')?.value,
    };

    if (this.isNewForm) {
      this.crearReporteLogo(request, this.archivo!);
    } else {
      this.actualizarReporteLogo(request, this.archivo);
    }
  }
  //#endregion
}
